import type { DbExecutor } from "../executor";
import { generateId } from "../ids";
import { ValidationError } from "./projectRepository";
import {
  validateScopePackage,
  validateScopePackageItem,
  type FinishTier,
  type PackageInclusion,
  type PackageKind,
  type PackageQuantitySource,
  type ScopePackage,
  type ScopePackageInput,
  type ScopePackageItem,
  type ScopePackageItemInput,
} from "../../domain/scopePackage";
import type { FeatureAction, QuantityUnit } from "../../domain/feature";

/**
 * Scope Package library (Phase 10, spec §21A).
 *
 * The library is SHARED, organization-owned data: packages outlive the
 * projects that used them, and editing one never reaches back into a
 * project (copy-on-use — a project keeps what was applied, not a link to
 * the current library).
 *
 * `orgId` is carried on every read and write so the SaaS build can scope a
 * library to an organization with no schema change.
 */
export const DEFAULT_ORG_ID = "default";

export class ScopePackageRepository {
  constructor(private readonly db: DbExecutor, private readonly orgId: string = DEFAULT_ORG_ID) {}

  async list(options: { includeArchived?: boolean; kind?: PackageKind } = {}): Promise<ScopePackage[]> {
    const where = ["org_id = ?"];
    const params: unknown[] = [this.orgId];
    if (!options.includeArchived) where.push("archived_at IS NULL");
    if (options.kind) {
      where.push("kind = ?");
      params.push(options.kind);
    }
    const rows = await this.db.select<PackageRow>(
      `SELECT * FROM scope_packages WHERE ${where.join(" AND ")} ORDER BY sort_order, name`,
      params,
    );
    return this.hydrate(rows);
  }

  async getById(id: string): Promise<ScopePackage | null> {
    const rows = await this.db.select<PackageRow>("SELECT * FROM scope_packages WHERE id = ?", [id]);
    if (rows.length === 0) return null;
    return (await this.hydrate(rows))[0];
  }

  /** Packages suggested for a room type — hints, never restrictions. */
  async listForRoomType(roomTypeId: string): Promise<ScopePackage[]> {
    const rows = await this.db.select<PackageRow>(
      `SELECT p.* FROM scope_packages p
         JOIN scope_package_room_types rt ON rt.package_id = p.id
        WHERE p.org_id = ? AND p.archived_at IS NULL AND rt.room_type_id = ?
        ORDER BY p.sort_order, p.name`,
      [this.orgId, roomTypeId],
    );
    return this.hydrate(rows);
  }

  private async hydrate(rows: PackageRow[]): Promise<ScopePackage[]> {
    if (rows.length === 0) return [];
    const ids = rows.map((r) => r.id);
    const placeholders = ids.map(() => "?").join(",");
    const itemRows = await this.db.select<ItemRow>(
      `SELECT * FROM scope_package_items WHERE package_id IN (${placeholders}) ORDER BY sort_order, rowid`,
      ids,
    );
    const roomTypeRows = await this.db.select<{ package_id: string; room_type_id: string }>(
      `SELECT package_id, room_type_id FROM scope_package_room_types
        WHERE package_id IN (${placeholders}) ORDER BY sort_order`,
      ids,
    );
    return rows.map((row) => ({
      id: row.id,
      orgId: row.org_id,
      name: row.name,
      kind: row.kind as PackageKind,
      description: row.description ?? "",
      finishTier: (row.finish_tier as FinishTier | null) ?? null,
      isSystem: Number(row.is_system) === 1,
      sortOrder: Number(row.sort_order),
      archivedAt: row.archived_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
      roomTypeIds: roomTypeRows.filter((rt) => rt.package_id === row.id).map((rt) => rt.room_type_id),
      items: itemRows.filter((i) => i.package_id === row.id).map(toItem),
    }));
  }

  async create(input: ScopePackageInput): Promise<ScopePackage> {
    const problems = validateScopePackage(input);
    if (problems.length > 0) throw new ValidationError(problems);
    const id = generateId();
    const now = new Date().toISOString();
    await this.db.transaction(async () => {
      const max = await this.db.select<{ m: number | null }>(
        "SELECT MAX(sort_order) AS m FROM scope_packages WHERE org_id = ?",
        [this.orgId],
      );
      await this.db.execute(
        `INSERT INTO scope_packages (id, org_id, name, kind, description, finish_tier, is_system, sort_order, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, 0, ?, ?, ?)`,
        [id, this.orgId, input.name.trim(), input.kind, input.description, input.finishTier, Number(max[0]?.m ?? 0) + 10, now, now],
      );
      await this.replaceRoomTypes(id, input.roomTypeIds);
    });
    return (await this.getById(id))!;
  }

  async update(id: string, input: ScopePackageInput): Promise<void> {
    const problems = validateScopePackage(input);
    if (problems.length > 0) throw new ValidationError(problems);
    await this.db.transaction(async () => {
      await this.db.execute(
        "UPDATE scope_packages SET name = ?, kind = ?, description = ?, finish_tier = ?, updated_at = ? WHERE id = ?",
        [input.name.trim(), input.kind, input.description, input.finishTier, new Date().toISOString(), id],
      );
      await this.replaceRoomTypes(id, input.roomTypeIds);
    });
  }

  private async replaceRoomTypes(packageId: string, roomTypeIds: string[]): Promise<void> {
    await this.db.execute("DELETE FROM scope_package_room_types WHERE package_id = ?", [packageId]);
    let order = 10;
    for (const roomTypeId of roomTypeIds) {
      await this.db.execute(
        "INSERT OR IGNORE INTO scope_package_room_types (package_id, room_type_id, sort_order) VALUES (?, ?, ?)",
        [packageId, roomTypeId, order],
      );
      order += 10;
    }
  }

  /** Duplicate the library entry (a new package, never a link to the old). */
  async duplicate(id: string, name: string): Promise<ScopePackage> {
    const source = await this.getById(id);
    if (!source) throw new ValidationError(["That package no longer exists."]);
    const created = await this.create({
      name: name.trim() === "" ? `${source.name} (copy)` : name,
      kind: source.kind,
      description: source.description,
      finishTier: source.finishTier,
      roomTypeIds: source.roomTypeIds,
    });
    for (const item of source.items) {
      await this.addItem(created.id, {
        itemType: item.itemType,
        featureDefinitionId: item.featureDefinitionId,
        action: item.action,
        objectDefinitionId: item.objectDefinitionId,
        objectCount: item.objectCount,
        assemblyTemplateId: item.assemblyTemplateId,
        quantitySource: item.quantitySource,
        quantityX100: item.quantityX100,
        quantityUnit: item.quantityUnit,
        preferredProductId: item.preferredProductId,
        inclusion: item.inclusion,
        childPackageId: item.childPackageId,
        notes: item.notes,
      });
    }
    return (await this.getById(created.id))!;
  }

  /** Archive hides a package from pickers; applications already made stand. */
  async archive(id: string): Promise<void> {
    await this.db.execute("UPDATE scope_packages SET archived_at = ?, updated_at = ? WHERE id = ?", [
      new Date().toISOString(),
      new Date().toISOString(),
      id,
    ]);
  }

  async restore(id: string): Promise<void> {
    await this.db.execute("UPDATE scope_packages SET archived_at = NULL, updated_at = ? WHERE id = ?", [
      new Date().toISOString(),
      id,
    ]);
  }

  async addItem(packageId: string, input: ScopePackageItemInput): Promise<ScopePackageItem> {
    const problems = validateScopePackageItem(input);
    if (problems.length > 0) throw new ValidationError(problems);
    if (input.childPackageId === packageId) {
      throw new ValidationError(["A package cannot include itself."]);
    }
    const id = generateId();
    const now = new Date().toISOString();
    const max = await this.db.select<{ m: number | null }>(
      "SELECT MAX(sort_order) AS m FROM scope_package_items WHERE package_id = ?",
      [packageId],
    );
    await this.db.execute(
      `INSERT INTO scope_package_items
         (id, package_id, item_type, feature_definition_id, action, object_definition_id, object_count,
          assembly_template_id, quantity_source, quantity_x100, quantity_unit, preferred_product_id,
          inclusion, child_package_id, notes, sort_order, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        id, packageId, input.itemType, input.featureDefinitionId, input.action, input.objectDefinitionId,
        input.objectCount, input.assemblyTemplateId, input.quantitySource, input.quantityX100,
        input.quantityUnit, input.preferredProductId, input.inclusion, input.childPackageId,
        input.notes, Number(max[0]?.m ?? 0) + 10, now, now,
      ],
    );
    await this.touch(packageId);
    const rows = await this.db.select<ItemRow>("SELECT * FROM scope_package_items WHERE id = ?", [id]);
    return toItem(rows[0]);
  }

  async updateItem(itemId: string, input: ScopePackageItemInput): Promise<void> {
    const problems = validateScopePackageItem(input);
    if (problems.length > 0) throw new ValidationError(problems);
    await this.db.execute(
      `UPDATE scope_package_items SET
         item_type = ?, feature_definition_id = ?, action = ?, object_definition_id = ?, object_count = ?,
         assembly_template_id = ?, quantity_source = ?, quantity_x100 = ?, quantity_unit = ?,
         preferred_product_id = ?, inclusion = ?, child_package_id = ?, notes = ?, updated_at = ?
       WHERE id = ?`,
      [
        input.itemType, input.featureDefinitionId, input.action, input.objectDefinitionId, input.objectCount,
        input.assemblyTemplateId, input.quantitySource, input.quantityX100, input.quantityUnit,
        input.preferredProductId, input.inclusion, input.childPackageId, input.notes,
        new Date().toISOString(), itemId,
      ],
    );
    const owner = await this.db.select<{ package_id: string }>(
      "SELECT package_id FROM scope_package_items WHERE id = ?",
      [itemId],
    );
    if (owner[0]) await this.touch(owner[0].package_id);
  }

  async removeItem(itemId: string): Promise<void> {
    const owner = await this.db.select<{ package_id: string }>(
      "SELECT package_id FROM scope_package_items WHERE id = ?",
      [itemId],
    );
    await this.db.execute("DELETE FROM scope_package_items WHERE id = ?", [itemId]);
    if (owner[0]) await this.touch(owner[0].package_id);
  }

  /** Reorder for readability only — order carries no estimating meaning. */
  async moveItem(itemId: string, direction: "up" | "down"): Promise<void> {
    const rows = await this.db.select<{ package_id: string; sort_order: number }>(
      "SELECT package_id, sort_order FROM scope_package_items WHERE id = ?",
      [itemId],
    );
    if (rows.length === 0) return;
    const { package_id: packageId, sort_order: order } = rows[0];
    const neighbour = await this.db.select<{ id: string; sort_order: number }>(
      direction === "up"
        ? "SELECT id, sort_order FROM scope_package_items WHERE package_id = ? AND sort_order < ? ORDER BY sort_order DESC LIMIT 1"
        : "SELECT id, sort_order FROM scope_package_items WHERE package_id = ? AND sort_order > ? ORDER BY sort_order LIMIT 1",
      [packageId, order],
    );
    if (neighbour.length === 0) return;
    await this.db.transaction(async () => {
      await this.db.execute("UPDATE scope_package_items SET sort_order = ? WHERE id = ?", [neighbour[0].sort_order, itemId]);
      await this.db.execute("UPDATE scope_package_items SET sort_order = ? WHERE id = ?", [order, neighbour[0].id]);
    });
    await this.touch(packageId);
  }

  private async touch(packageId: string): Promise<void> {
    await this.db.execute("UPDATE scope_packages SET updated_at = ? WHERE id = ?", [
      new Date().toISOString(),
      packageId,
    ]);
  }
}

interface PackageRow {
  id: string;
  org_id: string;
  name: string;
  kind: string;
  description: string | null;
  finish_tier: string | null;
  is_system: number;
  sort_order: number;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

interface ItemRow {
  id: string;
  package_id: string;
  item_type: string;
  feature_definition_id: string | null;
  action: string | null;
  object_definition_id: string | null;
  object_count: number;
  assembly_template_id: string | null;
  quantity_source: string;
  quantity_x100: number | null;
  quantity_unit: string | null;
  preferred_product_id: string | null;
  inclusion: string;
  child_package_id: string | null;
  notes: string;
  sort_order: number;
}

function toItem(row: ItemRow): ScopePackageItem {
  return {
    id: row.id,
    packageId: row.package_id,
    itemType: row.item_type === "package" ? "package" : "feature",
    featureDefinitionId: row.feature_definition_id,
    action: (row.action as FeatureAction | null) ?? null,
    objectDefinitionId: row.object_definition_id,
    objectCount: Number(row.object_count ?? 1),
    assemblyTemplateId: row.assembly_template_id,
    quantitySource: row.quantity_source as PackageQuantitySource,
    quantityX100: row.quantity_x100 === null ? null : Number(row.quantity_x100),
    quantityUnit: (row.quantity_unit as QuantityUnit | null) ?? null,
    preferredProductId: row.preferred_product_id,
    inclusion: row.inclusion as PackageInclusion,
    childPackageId: row.child_package_id,
    notes: row.notes ?? "",
    sortOrder: Number(row.sort_order),
  };
}
